/* eslint-disable react/no-multi-comp */
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents/Types/InterstateInputEventTypes';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import { formatNumberAndCents, invalidNumberDisplayValue } from '@makemydeal/dr-common-utils';
import * as dueAtSigningActionCreators from '@makemydeal/dr-dash-store/actions/dueAtSigningActionCreators';
import { DAS_OVERRIDES_FIELDS } from '@makemydeal/dr-dash-store/constants';
import * as dueAtSigningSelectors from '@makemydeal/dr-dash-store/selectors/dueAtSigningSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useDispatch, useSelector } from 'react-redux';
import { CASH_AMOUNT_PAID, CASH_DOWN, GENERIC_LAST_FIELD_CHANGED_ERROR } from '../constants';
import { dealLockHooks } from '../hooks';
import CashFieldLayout from './CashFieldLayout.interstate';
import { z } from 'zod';
import { useSummaryCashAmountPaid } from './hooks/useSummaryCashAmountPaid';
import type { FC, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

const DOWN_PAYMENT_MAX = 999999;
const DOWN_PAYMENT_MIN = 0;

export const CASH_AMOUNT_PAID_INPUT_PROPS = {
    inputPrefix: '$',
    decimalMaxLength: 2,
    decimalMinLength: 0,
    maxValue: DOWN_PAYMENT_MAX,
    minValue: DOWN_PAYMENT_MIN,
    autoInsertCommas: true,
    errorMessage: GENERIC_LAST_FIELD_CHANGED_ERROR,
    allowDecimal: true,
    strictSelectOnFocus: true,
    selectOnFocusSelector: featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap
};

const cashAmountPaidSchema = z.coerce.number().min(DOWN_PAYMENT_MIN).max(DOWN_PAYMENT_MAX);

export type CashFieldVariant = 'summary' | 'slideout';

type CashAmountPaidProps = {
    onInputErrors?: (message: string) => void;
    label?: string;
    variant?: CashFieldVariant;
};

type CashAmountPaidViewProps = {
    label: string;
    variant: CashFieldVariant;
    value: string;
    disabled: boolean;
    readOnly?: boolean;
    hasError: boolean;
    onChange: (event: InterstateOnChangeEvent<string>) => void;
    onBlur?: (event: InterstateOnChangeEvent<string>) => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
};

/**
 * Presentational cash input shared by the summary and slideout variants. The summary variant renders
 * the label natively on the field; the slideout variant renders a standalone heading above the field.
 */
const CashAmountPaidView: FC<CashAmountPaidViewProps> = ({
    label,
    variant,
    value,
    disabled,
    readOnly,
    hasError,
    onChange,
    onBlur,
    onKeyDown
}) => {
    const isSummaryVariant = variant === 'summary';
    // Unique per variant: the summary field stays mounted behind the open slideout, so both
    // editable inputs can be in the DOM at once and must not share an id/name/testid.
    const inputId = isSummaryVariant ? 'cash-amount-paid-input-summary' : 'text-input-prefix';
    const inputTestId = isSummaryVariant ? 'cash-amount-paid-input-summary' : 'cash-amount-paid-input';

    return (
        <CashFieldLayout variant={variant} label={label}>
            <NumericInput
                {...CASH_AMOUNT_PAID_INPUT_PROPS}
                id={inputId}
                name={inputId}
                data-testid={inputTestId}
                value={value}
                disabled={disabled}
                readOnly={readOnly}
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                hasError={hasError}
                label={isSummaryVariant ? label : undefined}
                displayLabel={isSummaryVariant}
                size={isSummaryVariant ? 'sm' : undefined}
            />
        </CashFieldLayout>
    );
};

/**
 * Slideout variant: the editable cash field updates the in-progress Due at Signing override and
 * triggers a preview recalculation (debounced). Changes are not committed to the deal until the
 * user applies them from the slideout.
 */
const SlideoutCashAmountPaid: FC<CashAmountPaidProps> = ({ onInputErrors, label }) => {
    const isDasLocked = useSelector(dueAtSigningSelectors.getIsDasLocked);
    const displayAmount = useSelector(dueAtSigningSelectors.getDownPaymentAmountWithFallback);
    const isTargetCashAmountPaid = useSelector(dueAtSigningSelectors.getTargetCashAmountPaidOverride);
    const isOnePayLease = useSelector(offerReduxSelectors.isOnePayLease);
    const deductDueAtSigning = useSelector(dealerSelectors.getIsDueAtSigninAmountDeduct);
    const isEquityAllocationOrderEnabled = useSelector(featureToggleSelectors.isEquityAllocationOrderEnabled);
    const dasErrors = useSelector(dueAtSigningSelectors.getDasErrors);
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();

    const [value, setValue] = useState(displayAmount);
    const timeout = useRef<NodeJS.Timeout | null>(null);

    // Cash down stays editable for a one-pay lease when Equity Allocation Order is enabled (DE752852).
    const disabled = isDasLocked || (isOnePayLease && deductDueAtSigning && !isEquityAllocationOrderEnabled) || isLocked;
    const parseResult = cashAmountPaidSchema.safeParse(value);
    const formattedValue = parseResult.success ? formatNumberAndCents(parseResult.data) : invalidNumberDisplayValue();
    const hasErrors = dasErrors.length > 0;

    useEffect(() => {
        return () => {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }
            timeout.current = null;
        };
    }, []);

    // Snap the input to the recalculated value when the user is not editing and DAS is idle
    // (e.g. after Apply or an equity allocation change); this discards typed values.
    useEffect(() => {
        if (timeout.current === null && !isDasLocked) {
            setValue(displayAmount);
        }
    }, [displayAmount, isDasLocked]);

    const handleBlurCashAmountPaid = (event: InterstateOnChangeEvent<string>) => {
        dispatch(dueAtSigningActionCreators.clearDasErrors());
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        const value = event.target.value;

        const safeParseAmount = cashAmountPaidSchema.safeParse(value);

        if (!safeParseAmount.success) {
            onInputErrors?.(safeParseAmount.error.issues[0].message);
            return;
        }

        const amount = safeParseAmount.data;
        setValue(amount);

        if (amount !== displayAmount) {
            timeout.current = setTimeout(() => {
                timeout.current = null;
                const dueAtSigningDispatch = dueAtSigningActionCreators.updateDasChanges(DAS_OVERRIDES_FIELDS.DOWNPAYMENT);
                dispatch(dueAtSigningDispatch(amount));
            }, 1500);
        }
    };

    const resolvedLabel = label ?? (isTargetCashAmountPaid ? CASH_AMOUNT_PAID : CASH_DOWN);

    return (
        <CashAmountPaidView
            label={resolvedLabel}
            variant="slideout"
            value={formattedValue}
            disabled={disabled}
            hasError={hasErrors}
            onChange={handleBlurCashAmountPaid}
        />
    );
};

/**
 * Summary variant: the editable cash field commits directly to the deal (offer-redux) down payment,
 * mirroring the legacy CashDownField. Edits are authoritative and recalculate the actual deal,
 * keeping the summary screen and the Due at Signing slideout in sync.
 */
const SummaryCashAmountPaid: FC<CashAmountPaidProps> = ({ label }) => {
    const viewProps = useSummaryCashAmountPaid({ label });

    return <CashAmountPaidView {...viewProps} variant="summary" />;
};

const CashAmountPaid: FC<CashAmountPaidProps> = (props) => {
    if (props.variant === 'summary') {
        return <SummaryCashAmountPaid {...props} />;
    }
    return <SlideoutCashAmountPaid {...props} />;
};

export default CashAmountPaid;
